'use client';

import { motion } from 'framer-motion';
import { ArrowRight, Megaphone, Users, ShoppingBag, Wallet } from 'lucide-react';
import Link from 'next/link';

const loopNodes = [
    { icon: Megaphone, label: 'Ad spend', position: 'top-0 left-1/2 -translate-x-1/2 -translate-y-1/2' },
    { icon: Users, label: 'Leads', position: 'top-1/2 right-0 translate-x-1/2 -translate-y-1/2' },
    { icon: ShoppingBag, label: 'Sales', position: 'bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2' },
    { icon: Wallet, label: 'Revenue', position: 'top-1/2 left-0 -translate-x-1/2 -translate-y-1/2' },
];

export default function StartProjectBanner() {
    return (
        <section className="relative w-full bg-white z-20 overflow-hidden" style={{ boxShadow: '0 -20px 50px rgba(84,96,90,0.15)' }}>
            <div className="relative w-full py-16 md:py-24 bg-white flex items-center justify-center min-h-[60vh] overflow-visible">
                <div className="container mx-auto px-4 md:px-8 max-w-7xl relative flex flex-col md:flex-row items-center justify-between gap-12 md:gap-8">

                        {/* Left text block */}
                        <div className="w-full md:w-[46%] flex flex-col items-center md:items-start text-center md:text-left">

                            <motion.h3
                                className="text-3xl md:text-4xl lg:text-[3.2rem] font-bold leading-[1.1] mb-5"
                                style={{
                                    fontFamily: "var(--font-clash), 'Clash Display', sans-serif",
                                    wordSpacing: '0.12em',
                                }}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-100px' }}
                                transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
                            >
                                <span className="block whitespace-nowrap" style={{ color: '#080400' }}>Your Marketing Should</span>
                                <span className="block mt-2" style={{ color: '#3730a3' }}>Pay You Back.</span>
                            </motion.h3>

                            <motion.p
                                className="text-base md:text-lg font-medium mb-8 max-w-sm leading-relaxed"
                                style={{
                                    fontFamily: "var(--font-manrope), 'Manrope', sans-serif",
                                    color: '#71717a',
                                }}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, delay: 0.35 }}
                            >
                                Every campaign, website, and strategy we build is designed with one goal in mind—delivering measurable business growth. Because marketing shouldn&apos;t just look good; it should generate leads, drive sales, and create lasting returns.
                            </motion.p>

                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.5 }}
                            >
                                <Link
                                    href="/contact"
                                    className="group relative inline-flex items-center gap-2.5 px-6 py-3 rounded-full overflow-hidden"
                                    style={{ background: 'linear-gradient(135deg, #00145B 0%, #3730A3 100%)' }}
                                >
                                    <span className="relative inline-flex w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#CEFF1A' }} />
                                    <span className="font-semibold text-sm tracking-wide text-white" style={{ fontFamily: "var(--font-clash), 'Clash Display', sans-serif" }}>
                                        let&apos;s talk
                                    </span>
                                    <ArrowRight className="w-4 h-4 stroke-[2.5] text-white transition-transform group-hover:translate-x-0.5" />
                                </Link>
                            </motion.div>
                        </div>

                        {/* Right — growth loop illustration */}
                        <motion.div
                            className="w-full md:w-[42%] flex justify-center md:justify-end"
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true, margin: '-100px' }}
                            transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
                        >
                            <div className="relative w-full max-w-[340px]">
                                {/* Founded-2026 badge */}
                                <motion.div
                                    className="absolute -top-4 -left-4 z-10 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5"
                                    style={{ background: '#CEFF1A', transform: 'rotate(-6deg)', boxShadow: '0 8px 20px rgba(206,255,26,0.35)' }}
                                    initial={{ opacity: 0, y: -10, rotate: -6 }}
                                    whileInView={{ opacity: 1, y: 0, rotate: -6 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: 0.6 }}
                                >
                                    <span className="text-[11px] font-bold tracking-wide" style={{ color: '#080400', fontFamily: "var(--font-clash), 'Clash Display', sans-serif" }}>
                                        est. 2026 · fresh eyes, real results
                                    </span>
                                </motion.div>

                                <div
                                    className="relative w-full rounded-2xl p-6 pt-8"
                                    style={{ background: '#080400', boxShadow: '0 20px 60px rgba(8,4,0,0.18)' }}
                                >
                                    <p className="text-xs mb-1" style={{ color: '#CEFF1A', fontFamily: "var(--font-manrope), Manrope, sans-serif" }}>
                                        The growth loop
                                    </p>
                                    <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.55)', fontFamily: "var(--font-manrope), Manrope, sans-serif" }}>
                                        Every campaign feeds the next
                                    </p>

                                    {/* Orbit diagram */}
                                    <div className="relative w-[190px] h-[190px] mx-auto mb-6">
                                        {/* dashed track */}
                                        <div
                                            className="absolute inset-0 rounded-full"
                                            style={{ border: '1.5px dashed rgba(255,255,255,0.16)' }}
                                        />

                                        {/* orbiting marker */}
                                        <motion.div
                                            className="absolute inset-0"
                                            animate={{ rotate: 360 }}
                                            transition={{ repeat: Infinity, duration: 8, ease: 'linear' }}
                                        >
                                            <span
                                                className="absolute -top-[3px] left-1/2 -translate-x-1/2 w-2 h-2 rounded-full"
                                                style={{ background: '#CEFF1A', boxShadow: '0 0 10px rgba(206,255,26,0.8)' }}
                                            />
                                        </motion.div>

                                        {/* center stat */}
                                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                                            <motion.div
                                                className="absolute w-20 h-20 rounded-full"
                                                style={{ background: 'rgba(206,255,26,0.12)' }}
                                                animate={{ scale: [1, 1.15, 1], opacity: [0.6, 0.9, 0.6] }}
                                                transition={{ repeat: Infinity, duration: 2.6, ease: 'easeInOut' }}
                                            />
                                            <p className="relative text-2xl font-bold text-white" style={{ fontFamily: "var(--font-clash), 'Clash Display', sans-serif" }}>
                                                +184%
                                            </p>
                                            <p className="relative text-[10px] tracking-wide" style={{ color: 'rgba(255,255,255,0.55)', fontFamily: "var(--font-manrope), Manrope, sans-serif" }}>
                                                ROI
                                            </p>
                                        </div>

                                        {/* stage nodes */}
                                        {loopNodes.map(({ icon: Icon, label, position }, i) => (
                                            <motion.div
                                                key={label}
                                                className={`absolute ${position} flex flex-col items-center gap-1`}
                                                initial={{ opacity: 0, scale: 0.6 }}
                                                whileInView={{ opacity: 1, scale: 1 }}
                                                viewport={{ once: true }}
                                                transition={{ duration: 0.5, delay: 0.5 + i * 0.12 }}
                                            >
                                                <span
                                                    className="flex items-center justify-center w-9 h-9 rounded-full"
                                                    style={{ background: '#151109', border: '1px solid rgba(206,255,26,0.35)' }}
                                                >
                                                    <Icon className="w-4 h-4" style={{ color: '#CEFF1A' }} strokeWidth={2} />
                                                </span>
                                                <span
                                                    className="text-[9px] font-medium whitespace-nowrap"
                                                    style={{ color: 'rgba(255,255,255,0.5)', fontFamily: "var(--font-manrope), Manrope, sans-serif" }}
                                                >
                                                    {label}
                                                </span>
                                            </motion.div>
                                        ))}
                                    </div>

                                    <div
                                        className="inline-flex items-center gap-2 rounded-full px-3 py-1.5"
                                        style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)' }}
                                    >
                                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#CEFF1A' }} />
                                        <span className="text-xs font-semibold text-white" style={{ fontFamily: "var(--font-manrope), Manrope, sans-serif" }}>
                                            Marketing that pays for itself
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                    </div>
            </div>
        </section>
    );
}
